/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import JSZip from "jszip";
import pako from "pako";
import { parseTarEntries } from "../Shared/TarUtils";

const ACCEPTED_EXTENSIONS = [".xml", ".proto"];

/**
 * Extracts all .xml and .proto files from a zip archive, including nested folders
 * @param zipFile The zip file to extract
 * @returns Array of extracted File objects (only .xml and .proto files)
 */
export async function extractZipFile(zipFile: File): Promise<File[]> {
  try {
    const zip = new JSZip();
    const zipData = await zip.loadAsync(zipFile);
    const extractedFiles: File[] = [];

    // JSZip.files already contains ALL files at any depth
    // The key is the full path (e.g., "folder1/folder2/file.xml")
    for (const [fullPath, zipEntry] of Object.entries(zipData.files)) {
      // Skip directories
      if (zipEntry.dir) {
        continue;
      }

      // Check if file has an accepted extension
      const lowerPath = fullPath.toLowerCase();
      const hasValidExtension = ACCEPTED_EXTENSIONS.some((ext) =>
        lowerPath.endsWith(ext)
      );

      if (!hasValidExtension) {
        continue; // Skip files that aren't .xml or .proto
      }

      // Get the file content as a Blob
      const blob = await zipEntry.async("blob");

      // Extract just the filename (not the full path)
      // e.g., "folder1/folder2/file.xml" -> "file.xml"
      const filename = fullPath.split("/").pop() || fullPath;

      // Create a File object with just the filename (not full path)
      const file = new File([blob], filename, {
        type: blob.type || "application/octet-stream",
      });

      extractedFiles.push(file);
    }

    return extractedFiles;
  } catch (error) {
    console.error("Failed to extract zip file:", error);
    throw new Error(`Failed to extract zip file: ${zipFile.name}`);
  }
}

/**
 * Extracts all .xml and .proto files from a .tar.gz archive, at any depth.
 *
 * Mirrors extractZipFile's contract: same accepted-extension filter, filenames
 * flattened to their basename, nested archives ignored.
 */
export async function extractTarGzFile(tarGzFile: File): Promise<File[]> {
  try {
    const arrayBuffer = await tarGzFile.arrayBuffer();
    const tarBuffer = pako.ungzip(new Uint8Array(arrayBuffer));
    const entries = parseTarEntries(tarBuffer);
    const extractedFiles: File[] = [];

    for (const entry of entries) {
      const lowerPath = entry.name.toLowerCase();
      const hasValidExtension = ACCEPTED_EXTENSIONS.some((ext) =>
        lowerPath.endsWith(ext)
      );
      if (!hasValidExtension) {
        continue; // Skip anything that isn't .xml or .proto
      }

      // Flatten to basename, matching the zip path's behavior.
      const filename = entry.name.split("/").pop() || entry.name;

      // entry.data is a view into the decompressed buffer; slice() copies it
      // so the File owns its own bytes.
      const file = new File([entry.data.slice()], filename, {
        type: "application/octet-stream",
      });

      extractedFiles.push(file);
    }

    return extractedFiles;
  } catch (error) {
    console.error("Failed to extract tar.gz file:", error);
    throw new Error(`Failed to extract tar.gz file: ${tarGzFile.name}`);
  }
}

/**
 * Checks if a file is a .tar.gz archive.
 */
export function isTarGzFile(file: File): boolean {
  return (
    file.type === "application/gzip" ||
    file.type === "application/x-gzip" ||
    file.name.toLowerCase().endsWith(".tar.gz")
  );
}

/**
 * Checks if a file is a zip archive
 * @param file The file to check
 * @returns True if the file is a zip archive
 */
export function isZipFile(file: File): boolean {
  return (
    file.type === "application/zip" ||
    file.type === "application/x-zip-compressed" ||
    file.name.toLowerCase().endsWith(".zip")
  );
}

/**
 * Processes an array of files, extracting any zip files
 * @param files Array of files to process
 * @returns Array of files with zip contents extracted (only .xml and .proto)
 */
export async function processFilesWithZipExtraction(
  files: File[]
): Promise<File[]> {
  const processedFiles: File[] = [];

  for (const file of files) {
    if (isZipFile(file)) {
      // Extract zip and add all contained .xml and .proto files (at any depth)
      const extractedFiles = await extractZipFile(file);
      processedFiles.push(...extractedFiles);
    } else if (isTarGzFile(file)) {
      // Extract tar.gz and add all contained .xml and .proto files
      const extractedFiles = await extractTarGzFile(file);
      processedFiles.push(...extractedFiles);
    } else {
      // Add non-zip files as-is
      processedFiles.push(file);
    }
  }

  return processedFiles;
}
